import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, MessageSquare, FileText, Calendar,
  Clock, MapPin, User, Heart, LogOut, ChevronDown, ChevronUp,
  Camera, X as XIcon, Loader2
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Feb 2026 — Session Photo thumbnail. Fetches bytes via an authenticated
// XHR as a Blob (img tags can't send Authorization headers), creates an
// object URL for the thumbnail, and revokes it on unmount. Photos are
// NEVER public — every byte read is proxied through our auth check.
function PhotoThumb({ photoId, onOpen }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let revoke;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/session-photos/${photoId}/file`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });
        const u = URL.createObjectURL(res.data);
        revoke = () => URL.revokeObjectURL(u);
        setBlobUrl(u);
      } catch (e) {
        setErr(e?.response?.status === 409 ? 'consent' : 'err');
      }
    })();
    return () => { if (revoke) revoke(); };
  }, [photoId]);
  if (err === 'consent') return null;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden border border-stone-200 bg-stone-100 hover:ring-2 hover:ring-primary/40 transition"
      data-testid={`parent-photo-thumb-${photoId}`}
    >
      {blobUrl ? (
        <img src={blobUrl} alt="session photo" className="w-full h-full object-cover" />
      ) : err ? (
        <div className="w-full h-full flex items-center justify-center text-stone-400 text-[11px]">unavailable</div>
      ) : (
        <div className="w-full h-full flex items-center justify-center text-stone-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}
    </button>
  );
}

const CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'general', label: 'General', color: 'bg-stone-100 text-stone-700' },
  { value: 'language', label: 'Language', color: 'bg-blue-100 text-blue-700' },
  { value: 'articulation', label: 'Articulation', color: 'bg-green-100 text-green-700' },
  { value: 'fluency', label: 'Fluency', color: 'bg-purple-100 text-purple-700' },
  { value: 'pragmatics', label: 'Pragmatics', color: 'bg-pink-100 text-pink-700' },
  { value: 'feeding', label: 'Feeding', color: 'bg-orange-100 text-orange-700' },
  { value: 'aac', label: 'AAC', color: 'bg-teal-100 text-teal-700' }
];

const SESSION_LOCATIONS = {
  clinic: 'Clinic',
  home: 'Home Visit',
  school: 'School',
  teletherapy: 'Teletherapy',
  community: 'Community'
};

export default function ParentFeedback({ onLogout }) {
  const navigate = useNavigate();
  const { childId } = useParams();
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});
  const [filterChild, setFilterChild] = useState('all');
  const [childName, setChildName] = useState('');
  const [error, setError] = useState(null);
  // Feb 2026 — Session Photo Sharing. Parent must grant photo consent
  // per child BEFORE the backend will serve photo bytes. We cache the
  // decision per child_id in local state so a single click gates the
  // whole visit without extra round-trips.
  const [photoConsentByChild, setPhotoConsentByChild] = useState({});
  const [photoConsentDialog, setPhotoConsentDialog] = useState({ open: false, childId: null, text: '' });
  const [photoConsentAck, setPhotoConsentAck] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { photoId, mime, blobUrl }
  
  // Safely parse user from localStorage
  let user = null;
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      user = JSON.parse(userStr);
    }
  } catch (e) {
    console.error('Failed to parse user from localStorage');
  }

  useEffect(() => {
    fetchFeedback();
    if (childId) {
      fetchChildName();
    }
  }, [childId]);

  const fetchChildName = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/children/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChildName(response.data.name);
      // Set filter to child name for filtering
      setFilterChild(response.data.name);
    } catch (error) {
      console.error('Failed to fetch child name');
    }
  };

  const fetchFeedback = async () => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API}/parent/feedback`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Show session notes + general feedback messages.
      // (Resource shares live in the Learn More / child dashboard.)
      const filtered = response.data.filter(
        (item) => item.type === 'session_note' || item.type === 'general_feedback',
      );
      setFeedback(filtered);

      // Silent read-receipt: any general_feedback we just loaded gets
      // marked as read on the server so the therapist's "N read" count
      // reflects reality. Ignore individual failures — this is fire-and-
      // forget best-effort. Only fires for items THIS user hasn't already
      // marked (server is also idempotent).
      const me = JSON.parse(localStorage.getItem('user') || 'null');
      if (me?.id) {
        filtered
          .filter((it) => it.type === 'general_feedback' && !(it.read_by || []).includes(me.id))
          .forEach((it) => {
            axios.post(`${API}/general-feedback/${it.id}/mark-read`, {}, {
              headers: { Authorization: `Bearer ${token}` },
            }).catch(() => { /* silent */ });
          });
      }
    } catch (error) {
      console.error('Failed to load feedback:', error);
      setError('Failed to load feedback');
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Feb 2026 — Load this child's photo-consent state lazily (only when
  // a parent actually sees photos in their feed). Memoised per child.
  const ensurePhotoConsentLoaded = async (cid) => {
    if (!cid || photoConsentByChild[cid]) return photoConsentByChild[cid];
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/session-photos/parent-consent/${cid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPhotoConsentByChild((prev) => ({ ...prev, [cid]: r.data }));
      return r.data;
    } catch (e) {
      return { has_consent: false };
    }
  };

  const openPhotoConsentDialog = async (cid) => {
    const consent = await ensurePhotoConsentLoaded(cid);
    setPhotoConsentDialog({ open: true, childId: cid, text: consent?.consent_text || '' });
    setPhotoConsentAck(false);
  };

  const confirmParentPhotoConsent = async () => {
    const cid = photoConsentDialog.childId;
    if (!cid || !photoConsentAck) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/session-photos/parent-consent`,
        { child_id: cid, consent: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPhotoConsentByChild((prev) => ({
        ...prev,
        [cid]: { ...(prev[cid] || {}), has_consent: true },
      }));
      setPhotoConsentDialog({ open: false, childId: null, text: '' });
      setPhotoConsentAck(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record consent');
    }
  };

  // Pre-load photo-consent status whenever we see feedback items that
  // carry shared photos — avoids a "click, nothing happens" moment.
  useEffect(() => {
    const childIds = new Set(
      feedback
        .filter((it) => it.type === 'general_feedback' && (it.photo_count || 0) > 0)
        .map((it) => it.child_id),
    );
    childIds.forEach((cid) => ensurePhotoConsentLoaded(cid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback]);

  const openPhoto = async (photoId, mime) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/session-photos/${photoId}/file`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(res.data);
      setLightbox({ photoId, mime, blobUrl });
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error('Please accept the photo-sharing consent first.');
      } else {
        toast.error('Could not open photo');
      }
    }
  };

  const closeLightbox = () => {
    if (lightbox?.blobUrl) URL.revokeObjectURL(lightbox.blobUrl);
    setLightbox(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get unique children for filter
  const uniqueChildren = [...new Set(feedback.map(f => f.child_name))];

  // Filter feedback by child
  const filteredFeedback = feedback.filter(item => {
    if (filterChild !== 'all' && item.child_name !== filterChild) return false;
    return true;
  });

  // Group by month
  const groupedByMonth = filteredFeedback.reduce((acc, item) => {
    const date = new Date(item.date);
    const monthKey = `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
    if (!acc[monthKey]) acc[monthKey] = [];
    acc[monthKey].push(item);
    return acc;
  }, {});

  const sessionNoteCount = filteredFeedback.length;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Heart className="w-6 h-6 text-primary fill-primary/20" />
              <span className="text-xl font-serif font-medium text-primary-900">PlayFirst</span>
            </div>
          </div>
          <Button variant="outline" onClick={onLogout} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center">
                <MessageSquare className="w-7 h-7 text-green-600" />
              </div>
              <div>
                <h1 className="text-3xl font-serif text-primary-900">
                  {childName ? `${childName}'s Feedback` : 'Therapy Feedback'}
                </h1>
                <p className="text-stone-500">
                  {childName ? `Session notes shared by your therapist for ${childName}` : 'Session notes shared by your therapist'}
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className="flex gap-4 mb-6">
              <Card className="p-4 bg-white rounded-2xl border border-stone-100">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-2xl font-semibold text-primary-900">{sessionNoteCount}</p>
                    <p className="text-xs text-stone-500">Session Notes Shared</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Filter - only child filter, no type filter needed */}
            {uniqueChildren.length > 1 && (
              <div className="flex flex-wrap gap-3">
                <Select value={filterChild} onValueChange={setFilterChild}>
                  <SelectTrigger className="w-40 rounded-full" data-testid="filter-child">
                    <User className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="All Children" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Children</SelectItem>
                    {uniqueChildren.map(child => (
                      <SelectItem key={child} value={child}>{child}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-stone-600">Loading feedback...</p>
            </div>
          ) : error ? (
            /* Error State */
            <Card className="p-12 text-center bg-white rounded-3xl border border-stone-100">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-xl font-serif text-primary-900 mb-2">Couldn't load feedback</h3>
              <p className="text-stone-500 mb-4">{error}</p>
              <Button onClick={fetchFeedback} className="rounded-full">
                Try Again
              </Button>
            </Card>
          ) : filteredFeedback.length === 0 ? (
            /* Empty State */
            <Card className="p-12 text-center bg-white rounded-3xl border border-stone-100">
              <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-stone-400" />
              </div>
              <h3 className="text-xl font-serif text-primary-900 mb-2">No feedback yet</h3>
              <p className="text-stone-500">
                When your therapist shares session notes or resources, they'll appear here
              </p>
            </Card>
          ) : (
            /* Feedback List */
            <div className="space-y-8">
              {Object.entries(groupedByMonth).map(([month, items]) => (
                <div key={month}>
                  <h3 className="text-lg font-serif text-primary-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary" />
                    {month}
                  </h3>
                  <div className="space-y-4">
                    {items.map((item, index) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                      >
                        {item.type === 'general_feedback' ? (
                          <Card
                            className={`bg-white rounded-2xl border ${
                              item.read ? 'border-stone-100' : 'border-amber-200'
                            } p-4`}
                            data-testid={`feedback-general-${item.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                <MessageSquare className="w-5 h-5 text-amber-700" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <Badge variant="secondary" className="text-xs">
                                    Therapist Update
                                  </Badge>
                                  <span className="text-xs text-stone-400">{item.child_name}</span>
                                  {!item.read && (
                                    <Badge className="text-[10px] bg-amber-200 text-amber-900">
                                      New
                                    </Badge>
                                  )}
                                </div>
                                {item.title && (
                                  <p className="font-medium text-primary-900 mb-1">{item.title}</p>
                                )}
                                <p className="text-sm text-stone-700 whitespace-pre-wrap">
                                  {item.message}
                                </p>
                                {/* Feb 2026 — Session Photo Sharing. Render
                                    thumbnails for photos the therapist
                                    attached to this update. Parent must
                                    accept photo-sharing consent before the
                                    backend will serve bytes. */}
                                {item.photo_count > 0 && (() => {
                                  const consent = photoConsentByChild[item.child_id];
                                  if (!consent?.has_consent) {
                                    return (
                                      <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900" data-testid={`photo-consent-prompt-${item.id}`}>
                                        <Camera className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1">
                                          <p className="font-medium">
                                            Your therapist shared {item.photo_count} photo{item.photo_count > 1 ? 's' : ''} with this update.
                                          </p>
                                          <p className="mt-0.5">Accept photo-sharing consent to view them securely.</p>
                                          <Button
                                            type="button"
                                            size="sm"
                                            className="mt-2 rounded-full h-7 text-xs"
                                            onClick={() => openPhotoConsentDialog(item.child_id)}
                                            data-testid={`photo-consent-open-${item.id}`}
                                          >
                                            Review &amp; accept
                                          </Button>
                                        </div>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div className="mt-3 flex flex-wrap gap-2" data-testid={`photo-grid-${item.id}`}>
                                      {(item.photos || []).map((ph) => (
                                        <PhotoThumb
                                          key={ph.id}
                                          photoId={ph.id}
                                          onOpen={() => openPhoto(ph.id, ph.mime)}
                                        />
                                      ))}
                                    </div>
                                  );
                                })()}
                                <p className="text-xs text-stone-400 mt-2">
                                  {formatDate(item.date)} • {item.therapist_name}
                                </p>
                              </div>
                            </div>
                          </Card>
                        ) : (
                        /* Session Note Card */
                        <Card 
                          className="bg-white rounded-2xl border border-stone-100 overflow-hidden"
                          data-testid={`feedback-note-${item.id}`}
                        >
                          <div 
                            className="p-4 cursor-pointer hover:bg-stone-50 transition-colors"
                            onClick={() => toggleExpand(item.id)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                  <FileText className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge variant="secondary" className="text-xs">Session Note</Badge>
                                    <span className="text-xs text-stone-400">{item.child_name}</span>
                                  </div>
                                  <p className="font-medium text-primary-900">{formatDate(item.date)}</p>
                                  {item.parent_summary && (
                                    <p className="text-sm text-stone-600 mt-1 line-clamp-2">
                                      {item.parent_summary}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-3 mt-2 text-xs text-stone-400">
                                    {item.session_location && (
                                      <span className="flex items-center gap-1">
                                        <MapPin className="w-3 h-3" />
                                        {SESSION_LOCATIONS[item.session_location] || item.session_location}
                                      </span>
                                    )}
                                    {item.session_duration > 0 && (
                                      <span className="flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {item.session_duration} min
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {expandedItems[item.id] ? (
                                <ChevronUp className="w-5 h-5 text-stone-400" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-stone-400" />
                              )}
                            </div>
                          </div>
                          
                          {/* Expanded Content */}
                          {expandedItems[item.id] && (
                            <div className="px-4 pb-4 pt-0 border-t border-stone-100">
                              <div className="pl-13 space-y-4 mt-4">
                                {/* Focus Categories */}
                                {item.focus_categories && item.focus_categories.length > 0 && (
                                  <div>
                                    <p className="text-xs font-medium text-stone-500 mb-2">Focus Areas</p>
                                    <div className="flex flex-wrap gap-2">
                                      {item.focus_categories.map((cat, i) => {
                                        const catInfo = CATEGORIES.find(c => c.value === cat);
                                        return (
                                          <Badge 
                                            key={i} 
                                            className={catInfo?.color || 'bg-stone-100 text-stone-700'}
                                          >
                                            {catInfo?.label || cat}
                                          </Badge>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Parent Summary */}
                                {item.parent_summary && (
                                  <div>
                                    <p className="text-xs font-medium text-stone-500 mb-2">Summary for You</p>
                                    <div className="p-3 bg-green-50 rounded-xl">
                                      <p className="text-sm text-stone-700">{item.parent_summary}</p>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Observation (if no parent summary) */}
                                {!item.parent_summary && item.observation && (
                                  <div>
                                    <p className="text-xs font-medium text-stone-500 mb-2">Session Notes</p>
                                    <p className="text-sm text-stone-600">{item.observation}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </Card>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Back Button */}
          <div className="mt-8 text-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/')}
              className="rounded-full"
            >
              Back to Dashboard
            </Button>
          </div>
        </motion.div>
      </main>

      {/* Feb 2026 — Parent photo-sharing consent dialog. One-time per
          child; recorded server-side with timestamp + version. */}
      <Dialog open={photoConsentDialog.open} onOpenChange={(o) => setPhotoConsentDialog((s) => ({ ...s, open: o }))}>
        <DialogContent className="sm:max-w-lg" data-testid="parent-photo-consent-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" />
              Photo sharing — your consent
            </DialogTitle>
            <DialogDescription className="text-sm text-stone-600 pt-1">
              Please review and confirm before viewing shared session photos.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl bg-stone-50 border border-stone-200 p-3 text-sm text-stone-800">
            {photoConsentDialog.text || "I consent to photographs relating to my child's therapy being uploaded, stored and securely shared with me through PlayFirst for therapy-related purposes. I understand that I may withdraw this consent for future photo sharing and may request deletion where applicable."}
          </div>
          <label className="flex items-start gap-2 mt-3 cursor-pointer">
            <Checkbox
              checked={photoConsentAck}
              onCheckedChange={(c) => setPhotoConsentAck(!!c)}
              data-testid="parent-photo-consent-ack"
            />
            <span className="text-sm text-stone-700">I accept the statement above.</span>
          </label>
          <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
            <Button variant="ghost" onClick={() => setPhotoConsentDialog({ open: false, childId: null, text: '' })} data-testid="parent-photo-consent-cancel">
              Not now
            </Button>
            <Button
              onClick={confirmParentPhotoConsent}
              disabled={!photoConsentAck}
              data-testid="parent-photo-consent-confirm"
            >
              Accept &amp; view photos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lightbox for full-size viewing. Blob URL is revoked on close so
          the photo bytes don't linger in memory. */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeLightbox}
          data-testid="parent-photo-lightbox"
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-2"
            aria-label="Close photo"
            data-testid="parent-photo-lightbox-close"
          >
            <XIcon className="w-5 h-5" />
          </button>
          {/* Lightbox for full-size viewing. Blob URL is revoked on close so
              the photo bytes don't linger in memory. */}
          <img
            src={lightbox.blobUrl}
            alt="session photo"
            className="max-w-[95vw] max-h-[90vh] rounded-xl shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
